import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  alpha,
  Avatar,
  CircularProgress,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import invoiceService, { FormattedInvoiceEmailMessage } from '../../services/invoiceService';
import gatewayWhatsappService from '../../services/gatewayWhatsappService';
import contactService from '../../services/contactService';
import { GatewayWhatsapp, Contact } from '../../types';
import mensagemService from '../../services/mensagemService';
import Swal from 'sweetalert2';
import SysEditor from '../syseditor/SysEditor';

interface EnviarInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  clientId: number;
}

const EnviarInvoiceModal: React.FC<EnviarInvoiceModalProps> = ({
  open,
  onClose,
  invoiceId,
  clientId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);

  const tabsConfig = [
    { label: 'WhatsApp', icon: <WhatsAppIcon />, color: '#25D366' },
    { label: 'E-mail', icon: <EmailIcon />, color: '#1976d2' },
    { label: 'SMS', icon: <SmsIcon />, color: '#ff9800' },
  ];

  // WhatsApp State
  const [gateways, setGateways] = useState<GatewayWhatsapp[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<string | number>('');
  const [selectedContact, setSelectedContact] = useState<string | number>('');
  const [enviarPdf, setEnviarPdf] = useState(true);
  const [message, setMessage] = useState('');

  // Email state
  const [emailContacts, setEmailContacts] = useState<Contact[]>([]);
  const [selectedEmailContact, setSelectedEmailContact] = useState<string | number>('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  useEffect(() => {
    if (open && invoiceId) {
      loadInitialData();
    }
  }, [open, invoiceId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Carregar gateways e contatos em paralelo
      const [gatewaysRes, contactsRes] = await Promise.all([
        gatewayWhatsappService.getActiveGatewaysWhatsapp(),
        contactService.getByClientId(clientId)
      ]);

      setGateways(gatewaysRes);

      // Filtrar apenas contatos com WhatsApp
      const whatsappContacts = (contactsRes.data || []).filter((c: any) => c.is_whatsapp);
      setContacts(whatsappContacts);

      // Filtrar e-mails
      const mails = (contactsRes.data || []).filter((c: any) => c.tipo === 'email' || (c.email && c.email.includes('@')));
      setEmailContacts(mails);

      // Pré-selecionar gateway padrão se existir
      const defaultGw = gatewaysRes.find(g => g.is_default);
      if (defaultGw) setSelectedGateway(defaultGw.id);

      // Pré-selecionar primeiro contato se existir
      if (whatsappContacts.length > 0) setSelectedContact(whatsappContacts[0].id);
      if (mails.length > 0) setSelectedEmailContact(mails[0].id);

      // Carregar mensagem formatada
      await loadFormattedMessages();
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      Swal.fire('Erro', 'Não foi possível carregar os dados iniciais.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFormattedMessages = async () => {
    setLoadingMsg(true);
    try {
      // WhatsApp message
      const wpResponse = await invoiceService.getFormattedMessage(invoiceId, 'enviar_fatura', 'whatsapp');
      if (wpResponse.success && wpResponse.data) {
        setMessage(wpResponse.data as string);
      }

      // E-mail: assunto do template (email_subject) + corpo (custom_email_message) — ver InvoiceService.getFormattedMessage
      const emailResponse = await invoiceService.getFormattedMessage(invoiceId, 'enviar_fatura', 'email');
      if (emailResponse.success && emailResponse.data) {
        const data = emailResponse.data;
        const isStructured = (v: unknown): v is FormattedInvoiceEmailMessage =>
          typeof v === 'object' &&
          v !== null &&
          'subject' in v &&
          'body' in v &&
          typeof (v as FormattedInvoiceEmailMessage).subject === 'string' &&
          typeof (v as FormattedInvoiceEmailMessage).body === 'string';

        if (isStructured(data)) {
          setEmailSubject(data.subject);
          setEmailMessage(data.body);
        } else if (typeof data === 'string') {
          let content = data;
          if (content.startsWith('Assunto: ')) {
            const lines = content.split('\n\n');
            setEmailSubject(lines[0].replace('Assunto: ', ''));
            setEmailMessage(lines.slice(1).join('\n\n'));
          } else {
            setEmailSubject(`Fatura #${invoiceId}`);
            setEmailMessage(content);
          }
        }
      }
    } catch (error: any) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoadingMsg(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedGateway) {
      Swal.fire('Aviso', 'Selecione um gateway WhatsApp.', 'warning');
      return;
    }
    const contact = contacts.find(c => c.id === selectedContact);
    if (!contact) {
      Swal.fire('Aviso', 'Selecione um contato.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await mensagemService.createMensagem({
        tipo: 'whatsapp',
        destino: contact.id,
        conteudo: message,
        cliente_id: clientId,
        gateway_id: Number(selectedGateway),
        fatura_id: invoiceId,
        enviar_fatura_pdf: enviarPdf,
        sendNow: true
      });

      if (res.success) {
        Swal.fire('Sucesso', 'WhatsApp enviado com sucesso!', 'success');
        onClose();
      } else {
        Swal.fire('Erro', res.message || 'Erro ao enviar mensagem.', 'error');
      }
    } catch (error: any) {
      Swal.fire('Erro', error.message || 'Erro ao enviar WhatsApp.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    const contact = emailContacts.find(c => c.id === selectedEmailContact);
    if (!contact) {
      Swal.fire('Aviso', 'Selecione um contato de e-mail.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const res = await mensagemService.createMensagem({
        tipo: 'email',
        destino: contact.id,
        conteudo: `Assunto: ${emailSubject}\n\n${emailMessage}`,
        cliente_id: clientId,
        fatura_id: invoiceId,
        enviar_fatura_pdf: enviarPdf,
        sendNow: true
      });

      if (res.success) {
        Swal.fire('Sucesso', 'E-mail enviado com sucesso!', 'success');
        onClose();
      } else {
        Swal.fire('Erro', res.message || 'Erro ao enviar e-mail.', 'error');
      }
    } catch (error: any) {
      Swal.fire('Erro', error.message || 'Erro ao enviar e-mail.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog fullScreen={isMobile} open={open} onClose={onClose} maxWidth={tabIndex === 1 ? 'md' : 'sm'} fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, width: 40, height: 40 }}>
              <DescriptionIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Enviar Fatura #{invoiceId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Notifique o cliente via WhatsApp, E-mail ou SMS
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Tabs
        value={tabIndex}
        onChange={(_, val) => setTabIndex(val)}
        variant="fullWidth"
        sx={{
          px: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.82rem',
            minHeight: 42,
            gap: 0.8
          },
        }}
      >
        {tabsConfig.map((tab, idx) => (
          <Tab
            key={tab.label}
            icon={React.cloneElement(tab.icon as React.ReactElement, {
              sx: {
                fontSize: 17,
                color: tabIndex === idx ? tab.color : 'text.secondary'
              }
            })}
            iconPosition="start"
            label={tab.label}
          />
        ))}
      </Tabs>

      <DialogContent sx={{ minHeight: '300px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabIndex === 0 && (
              <Box sx={{ mt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>Gateway WhatsApp</InputLabel>
                    <Select
                      value={selectedGateway}
                      label="Gateway WhatsApp"
                      onChange={(e) => setSelectedGateway(e.target.value)}
                      disabled={loading}
                      size="small"
                    >
                      {gateways.map((gw) => (
                        <MenuItem key={gw.id} value={gw.id}>
                          {gw.nome} ({gw.tipo})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>Contato</InputLabel>
                    <Select
                      value={selectedContact}
                      label="Contato"
                      onChange={(e) => setSelectedContact(e.target.value)}
                      disabled={loading}
                      size="small"
                    >
                      {contacts.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.tipo.charAt(0).toUpperCase() + c.tipo.slice(1)} - {c.numero || c.email} {c.is_default ? '(Padrão)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  label="Mensagem"
                  multiline
                  rows={10}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  disabled={loading || loadingMsg}
                  InputProps={{
                    endAdornment: loadingMsg && <CircularProgress size={20} />
                  }}
                  size="small"
                />

                <Box sx={{ px: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enviarPdf}
                        onChange={(e) => setEnviarPdf(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" fontWeight={600}>Enviar PDF da Fatura</Typography>}
                  />
                </Box>
              </Box>
            )}

            {tabIndex === 1 && (
              <Box sx={{ mt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Contato (E-mail)</InputLabel>
                  <Select
                    value={selectedEmailContact}
                    label="Contato (E-mail)"
                    onChange={(e) => setSelectedEmailContact(e.target.value)}
                    disabled={loading}
                    size="small"
                  >
                    {emailContacts.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.tipo.charAt(0).toUpperCase() + c.tipo.slice(1)} - {c.email}
                      </MenuItem>
                    ))}
                    {emailContacts.length === 0 && (
                      <MenuItem value="" disabled>Nenhum email encontrado</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Assunto"
                  helperText="Preenchido com o assunto do template de notificação (campo email_subject), com variáveis já aplicadas."
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  disabled={loading || loadingMsg}
                  size="small"
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Mensagem
                  </Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      opacity: loading || loadingMsg ? 0.7 : 1,
                      pointerEvents: loading || loadingMsg ? 'none' : 'auto',
                    }}
                  >
                    <SysEditor
                      value={emailMessage}
                      onChange={setEmailMessage}
                      height={280}
                      disabled={loading || loadingMsg}
                      placeholder="Conteúdo do e-mail"
                    />
                  </Box>
                </Box>

                <Box sx={{ px: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enviarPdf}
                        onChange={(e) => setEnviarPdf(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" fontWeight={600}>Enviar PDF da Fatura</Typography>}
                  />
                </Box>
              </Box>
            )}

            {tabIndex === 2 && (
              <Box sx={{ mt: 2, p: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Conteúdo da aba SMS em desenvolvimento... (conforme solicitado, focar no WhatsApp agora)
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        {tabIndex === 0 && (
          <Button
            variant="contained"
            color="success"
            startIcon={<SendIcon />}
            onClick={handleSendWhatsApp}
            disabled={loading || !selectedGateway || !selectedContact}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: theme.shadows[2] }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar WhatsApp'}
          </Button>
        )}
        {tabIndex === 1 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSendEmail}
            disabled={loading || !selectedEmailContact}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: theme.shadows[2] }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar E-mail'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnviarInvoiceModal;
